import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { logEvent } from "../utils/analytics";
import { haptic } from "../../utils/nativeBridge";
import { TrainingBadge } from "./TrainingBadge";

/** Minimal markdown → React for chat messages */
function MdText({ text }) {
  const lines = text.split("\n");
  return lines.map((line, li) => {
    // Headings
    if (/^###\s/.test(line)) return <div key={li} style={{fontWeight:700,fontSize:14,marginTop:li?8:0,marginBottom:2}}>{renderInline(line.replace(/^###\s/,""))}</div>;
    if (/^##\s/.test(line)) return <div key={li} style={{fontWeight:700,fontSize:15,marginTop:li?10:0,marginBottom:2}}>{renderInline(line.replace(/^##\s/,""))}</div>;
    // Bullet points
    if (/^[-*]\s/.test(line.trim())) return <div key={li} style={{paddingLeft:12,marginTop:2,display:"flex",gap:6}}><span style={{opacity:0.5}}>•</span><span>{renderInline(line.trim().replace(/^[-*]\s/,""))}</span></div>;
    // Numbered lists
    if (/^\d+\.\s/.test(line.trim())) { const m=line.trim().match(/^(\d+)\.\s(.*)/); return <div key={li} style={{paddingLeft:12,marginTop:2,display:"flex",gap:6}}><span style={{opacity:0.5}}>{m[1]}.</span><span>{renderInline(m[2])}</span></div>; }
    // Empty line
    if (!line.trim()) return <div key={li} style={{height:6}}/>;
    // Normal paragraph
    return <div key={li} style={{marginTop:li?2:0}}>{renderInline(line)}</div>;
  });
}
function renderInline(text) {
  // Bold **text** and __text__
  const parts = text.split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
  return parts.map((p, i) => {
    if (/^\*\*(.+)\*\*$/.test(p)) return <strong key={i}>{p.slice(2,-2)}</strong>;
    if (/^__(.+)__$/.test(p)) return <strong key={i}>{p.slice(2,-2)}</strong>;
    return p;
  });
}

/**
 * AskBAM — floating chat widget for the BAM AI coaching assistant.
 *
 * Appears as a small button in the bottom-right corner. Click to open
 * a chat panel. Persists conversation history in the current session.
 */

const WELCOME_MESSAGE = {
  role: "assistant",
 content: "What's up, Coach? I'm the BAM coaching brain: ask me anything about drills, practice design, player development, or coaching methodology. I'll answer using BAM's frameworks and Coleman's approach.",
};

export function AskBAM({ dark }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      if (!hasOpened) {
        logEvent("ask_bam_opened");
        setHasOpened(true);
      }
    }
  }, [open, hasOpened]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    logEvent("ask_bam_question", { length: text.length });

    try {
      // Send conversation history (skip the welcome message)
      // Errors aren't conversation. Each earlier reply goes back with the
      // signature the server gave it; the server drops any it didn't sign.
      const history = messages
        .filter((m) => m !== WELCOME_MESSAGE && !m.isError)
        .map((m) => (m.role === "assistant"
          ? { role: m.role, content: m.content, sig: m.sig }
          : { role: m.role, content: m.content }));

      // The server checks who's asking, from the session, not from the body.
      let token = null;
      try {
        const { supabase } = await import("../../supabaseClient");
        const { data } = await supabase.auth.getSession();
        token = data?.session?.access_token || null;
      } catch {}
      const res = await fetch("/api/ask-bam", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: text, history }),
      });

      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        const wait = data.retryAfter || 10;
 throw new Error(`You're sending messages too quickly: wait ${wait}s.`);
      }
      if (res.status === 401 || res.status === 403) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Ask BAM isn't available on your account.");
      }
      if (res.status === 503) {
        // The AI kill switch (PQ-14): say so plainly rather than "error".
        const data = await res.json().catch(() => ({}));
        if (data.code === "ai_paused") throw new Error(data.error);
      }
      if (!res.ok) {
        throw new Error(`Server error (${res.status})`);
      }

      const data = await res.json();
      const reply = data.reply || "Sorry, I couldn't generate a response. Try rephrasing your question.";
      const citations = Array.isArray(data.citations) ? data.citations : [];

      setMessages((prev) => [...prev, { role: "assistant", content: reply, citations, sig: data.sig || null }]);
    } catch (e) {
      console.error("[AskBAM]", e);
      // Surface rate-limit messages verbatim (they're user-facing by design);
      // for other errors give the generic "try again" line.
      const msg = String(e?.message || "");
 const friendly = (msg.includes("too quickly") || msg.includes("Ask BAM") || msg.startsWith("Sign in") || msg.startsWith("AI features are paused"))
        ? msg : "Something went wrong: the AI service might be temporarily unavailable. Try again in a moment.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: friendly, isError: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const bg = dark ? "#111" : "#fff";
  const border = dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const textColor = dark ? "#e8e8e8" : "#111";
  const textDim = dark ? "#888" : "#666";
  const inputBg = dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)";

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div
          style={{
            position: "fixed",
            // Stack above the floating button + safe-area inset so the
            // panel doesn't crash into the home-indicator on phones.
            bottom: "calc(max(20px, env(safe-area-inset-bottom, 0px)) + 60px)",
            right: "max(20px, env(safe-area-inset-right, 0px))",
            /* Drop hardcoded 380px width — on a 375px iPhone viewport the
               panel was overflowing 5px and forcing horizontal scroll.
               min() caps to either 380px (desktop) or viewport minus
               safe gutters (mobile). 32px total leaves 16px on each
               side for breathing room. */
            width: "min(380px, calc(100vw - 32px))",
            height: 520,
            maxHeight: "calc(100vh - 120px)",
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: 16,
            boxShadow: dark
              ? "0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03)"
              : "0 20px 60px rgba(0,0,0,0.15)",
            zIndex: 8000,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily: "'DM Sans', sans-serif",
            animation: "nbSlide .2s cubic-bezier(.2,.8,.2,1)",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "14px 16px",
              borderBottom: `1px solid ${border}`,
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: dark ? "rgba(226,221,159,0.03)" : "rgba(226,221,159,0.06)",
              flexShrink: 0,
            }}
          >
            <img
              src={BAM_LOGO_PNG}
              alt="BAM"
              style={{ width: 26, height: 26, objectFit: "contain", filter: "drop-shadow(0 0 8px rgba(226,221,159,0.3))" }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: textColor, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                Ask BAM <Sparkles size={12} color={GOLD} />
                <TrainingBadge variant="inline" />
              </div>
              <div style={{ fontSize: 10, color: textDim }}>AI coaching assistant</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: textDim,
                padding: 4,
              }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflow: "auto",
              padding: "14px 14px 8px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                  gap: 8,
                }}
              >
                {msg.role === "assistant" && (
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      background: `${GOLD}20`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    <img src={BAM_LOGO_PNG} alt="" style={{ width: 14, height: 14, objectFit: "contain" }} />
                  </div>
                )}
                <div style={{ maxWidth: "80%", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div
                    style={{
                      padding: "10px 14px",
                      borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                      background: msg.role === "user"
                        ? GOLD
                        : msg.isError
                        ? (dark ? "rgba(224,96,96,0.1)" : "rgba(224,96,96,0.08)")
                        : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                      color: msg.role === "user" ? "#111" : msg.isError ? "#ff9d9d" : textColor,
                      fontSize: 13,
                      lineHeight: 1.55,
                      fontWeight: msg.role === "user" ? 600 : 400,
                      wordBreak: "break-word",
                    }}
                  >
                    {msg.role === "user" ? msg.content : <MdText text={msg.content} />}
                  </div>
                  {/* Citations — small chips below the assistant bubble.
                      Renders only when retrieval surfaced sources; tells
                      the coach which insight/drill/doc the answer leaned
                      on. Click-through to the underlying drill/file is
                      a follow-up; for now they're informational. */}
                  {msg.role === "assistant" && !msg.isError && Array.isArray(msg.citations) && msg.citations.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 2 }}>
                      {msg.citations.map((cit, ci) => (
                        <div key={ci}
                          title={cit.title}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            padding: "3px 9px", borderRadius: 8,
                            background: dark ? "rgba(226,221,159,0.06)" : "rgba(226,221,159,0.18)",
                            border: `1px solid ${dark ? "rgba(226,221,159,0.18)" : "rgba(226,221,159,0.45)"}`,
                            fontSize: 10.5, color: dark ? "rgba(226,221,159,0.85)" : "#5a4f1f",
                            maxWidth: 280,
                          }}>
                          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", opacity: 0.72 }}>
                            {cit.source}
                          </span>
                          <span style={{
                            fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {cit.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <div
                  style={{
                    width: 24, height: 24, borderRadius: 6,
                    background: `${GOLD}20`, display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, marginTop: 2,
                  }}
                >
                  <img src={BAM_LOGO_PNG} alt="" style={{ width: 14, height: 14, objectFit: "contain" }} />
                </div>
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: "14px 14px 14px 4px",
                    background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                    fontSize: 13, color: textDim,
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: GOLD, animation: "bamBootBar 1s infinite" }} />
                  Thinking...
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div
            style={{
              padding: "10px 12px",
              borderTop: `1px solid ${border}`,
              display: "flex",
              gap: 8,
              alignItems: "flex-end",
              flexShrink: 0,
            }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything about coaching..."
              rows={1}
              enterKeyHint="send"
              autoCapitalize="sentences"
              style={{
                flex: 1,
                background: inputBg,
                border: `1px solid ${border}`,
                borderRadius: 10,
                padding: "10px 12px",
                /* iOS Safari/WKWebView zooms inputs with font-size < 16px on
                   focus, which jumpily reflows the entire chat panel. Pin
                   to 16px on native; the layout is already tuned for it.
                   font-size:16px on the textarea reads slightly bigger on
                   web but the chat panel is roomy enough to absorb it. */
                fontSize: 16,
                color: textColor,
                outline: "none",
                fontFamily: "inherit",
                resize: "none",
                maxHeight: 100,
                boxSizing: "border-box",
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              aria-label="Send message"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: input.trim() && !loading ? GOLD : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"),
                border: "none",
                cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "all .15s ease",
              }}
            >
              <Send size={14} color={input.trim() && !loading ? "#111" : textDim} />
            </button>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        className="bam-askbam-floater"
        onClick={() => { haptic("light"); setOpen((o) => !o); }}
        aria-label={open ? "Close Ask BAM" : "Open Ask BAM"}
        style={{
          position: "fixed",
          // Respect iOS home-indicator safe area so the floating button
          // doesn't end up half-buried under the gesture pill on phones
          // with a dynamic bottom inset. max() picks the larger of 20px
          // or the current safe-area inset, so desktop is unchanged.
          bottom: "max(20px, env(safe-area-inset-bottom, 0px))",
          right: "max(20px, env(safe-area-inset-right, 0px))",
          width: 52,
          height: 52,
          borderRadius: 16,
          background: open ? (dark ? "#222" : "#e0e0e0") : `linear-gradient(135deg, ${GOLD}, #d4ce8a)`,
          border: open ? `1px solid ${border}` : "none",
          boxShadow: open ? "none" : `0 8px 32px ${GOLD}40`,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 8001,
          transition: "all .2s ease",
        }}
      >
        {open ? (
          <X size={20} color={textDim} />
        ) : (
          <MessageCircle size={22} color="#111" fill="#111" />
        )}
      </button>
    </>
  );
}
